import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { useAdminEmployees } from '@/common/hooks/useAdminEmployees'
import { fetchApi } from '@/common/lib/fetchApi'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { colors } from '@/core/styles/tokens'

interface PreviewEntry {
  date: string
  hours_worked: number
  holiday_hours: number
  vacation_hours: number
  standard_hours: number
  flsa_overtime_hours: number
  straight_overtime_hours: number
  day_total: number
}

interface PreviewResponse {
  start_date: string
  end_date: string
  entries: PreviewEntry[]
  total_hours: number
  total_standard: number
  total_flsa_overtime: number
  total_straight_overtime: number
}

interface ImportedTimesheetInfo {
  timesheet_id: number
  start_date: string
  end_date: string
  status: string
  total_hours: number
  entry_count: number
  entries_added: number
  entries_updated: number
  was_created: boolean
}

interface ImportResponse {
  employee_id: number
  timesheets: ImportedTimesheetInfo[]
  total_hours: number
  total_entry_count: number
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function ImportPage() {
  const [employeeId, setEmployeeId] = useState<number | ''>('')
  const [rawText, setRawText] = useState('')
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [importResult, setImportResult] = useState<ImportResponse | null>(null)

  const { data: employees } = useAdminEmployees()

  const previewMutation = useMutation({
    mutationFn: (body: { employee_id: number; raw_text: string }) =>
      fetchApi<PreviewResponse>('/api/org/import-timesheet/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setPreview(data)
      setImportResult(null)
    },
  })

  const importMutation = useMutation({
    mutationFn: (body: { employee_id: number; raw_text: string }) =>
      fetchApi<ImportResponse>('/api/org/import-timesheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setImportResult(data)
      setPreview(null)
    },
  })

  const handlePreview = () => {
    if (!employeeId || !rawText.trim()) return
    previewMutation.mutate({ employee_id: employeeId, raw_text: rawText })
  }

  const handleImport = () => {
    if (!employeeId || !rawText.trim()) return
    importMutation.mutate({ employee_id: employeeId, raw_text: rawText })
  }

  const canPreview = employeeId !== '' && rawText.trim().length > 0

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <Stack gap="16">
        <Text size="xl" weight="bold">
          Import Historical Timesheet
        </Text>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, maxWidth: 600 }}>
            {/* Employee selector */}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
              Employee
            </label>
            <select
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value ? Number(e.target.value) : '')
                setPreview(null)
                setImportResult(null)
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: `1px solid ${colors.gray20}`,
                fontSize: 14,
                marginBottom: 16,
              }}
            >
              <option value="">Select employee...</option>
              {employees?.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} ({emp.email})
                </option>
              ))}
            </select>

            {/* Paste area */}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
              Paste timesheet data
            </label>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value)
                setPreview(null)
                setImportResult(null)
              }}
              placeholder="Paste a single pay period block from the Excel spreadsheet here..."
              rows={16}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: `1px solid ${colors.gray20}`,
                fontSize: 13,
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button
                onClick={handlePreview}
                disabled={!canPreview || previewMutation.isPending}
                style={{
                  padding: '8px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: canPreview ? colors.sea : colors.gray50,
                  color: colors.white,
                  fontSize: 14,
                  cursor: canPreview ? 'pointer' : 'default',
                }}
              >
                {previewMutation.isPending ? 'Parsing...' : 'Preview'}
              </button>

              {preview && (
                <button
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: colors.success,
                    color: colors.white,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  {importMutation.isPending ? 'Importing...' : 'Import'}
                </button>
              )}
            </div>

            {previewMutation.isError && (
              <div style={{ marginTop: 12, padding: 12, background: colors.errorBackground, border: `1px solid ${colors.errorOutline}`, borderRadius: 6, color: colors.error, fontSize: 14 }}>
                {(previewMutation.error as Error).message}
              </div>
            )}

            {importMutation.isError && (
              <div style={{ marginTop: 12, padding: 12, background: colors.errorBackground, border: `1px solid ${colors.errorOutline}`, borderRadius: 6, color: colors.error, fontSize: 14 }}>
                {(importMutation.error as Error).message}
              </div>
            )}

            {importResult && (
              <div style={{ marginTop: 12, padding: 12, background: colors.successBackground, border: `1px solid ${colors.successOutline}`, borderRadius: 6, color: colors.success, fontSize: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Imported successfully! {importResult.total_entry_count} entries ({importResult.total_hours.toFixed(2)} total hours) across {importResult.timesheets.length} timesheet{importResult.timesheets.length > 1 ? 's' : ''}:
                </div>
                {importResult.timesheets.map((ts) => {
                  const action = ts.was_created ? 'created' : 'updated'
                  const parts: string[] = []
                  if (ts.entries_added > 0) parts.push(`${ts.entries_added} added`)
                  if (ts.entries_updated > 0) parts.push(`${ts.entries_updated} updated`)
                  return (
                    <div key={ts.timesheet_id} style={{ marginLeft: 8 }}>
                      Timesheet #{ts.timesheet_id} ({action}): {ts.start_date} to {ts.end_date} — {parts.join(', ')}, {ts.total_hours.toFixed(2)} hours ({ts.status})
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Preview table */}
        {preview && (
          <div style={{ marginTop: 8 }}>
            <Text size="lg" weight="bold">
              Preview: {preview.start_date} to {preview.end_date}
            </Text>
            <table style={{ width: '100%', maxWidth: 700, borderCollapse: 'collapse', marginTop: 8, fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.gray20}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Hours Worked</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Holiday</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>PTO</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.entries.map((entry) => (
                  <tr key={entry.date} style={{ borderBottom: `1px solid ${colors.gray10}` }}>
                    <td style={{ padding: '4px 8px' }}>{formatDate(entry.date)}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{entry.hours_worked > 0 ? entry.hours_worked.toFixed(2) : ''}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{entry.holiday_hours > 0 ? entry.holiday_hours.toFixed(2) : ''}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{entry.vacation_hours > 0 ? entry.vacation_hours.toFixed(2) : ''}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600 }}>{entry.day_total > 0 ? entry.day_total.toFixed(2) : ''}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${colors.gray20}`, fontWeight: 600 }}>
                  <td style={{ padding: '6px 8px' }} colSpan={4}>Total</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px' }}>{preview.total_hours.toFixed(2)}</td>
                </tr>
                <tr style={{ fontWeight: 500, fontSize: 13, color: colors.gray70 }}>
                  <td style={{ padding: '2px 8px' }} colSpan={4}>Standard</td>
                  <td style={{ textAlign: 'right', padding: '2px 8px' }}>{preview.total_standard.toFixed(2)}</td>
                </tr>
                {preview.total_flsa_overtime > 0 && (
                  <tr style={{ fontWeight: 500, fontSize: 13, color: colors.error }}>
                    <td style={{ padding: '2px 8px' }} colSpan={4}>OT (1.5x)</td>
                    <td style={{ textAlign: 'right', padding: '2px 8px' }}>{preview.total_flsa_overtime.toFixed(2)}</td>
                  </tr>
                )}
                {preview.total_straight_overtime > 0 && (
                  <tr style={{ fontWeight: 500, fontSize: 13, color: colors.caution }}>
                    <td style={{ padding: '2px 8px' }} colSpan={4}>Straight OT (1.0x)</td>
                    <td style={{ textAlign: 'right', padding: '2px 8px' }}>{preview.total_straight_overtime.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/import')({
  component: ImportPage,
})
